import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  keycloak,
  initOptions,
  TOKEN_REFRESH_INTERVAL,
  MIN_TOKEN_VALIDITY,
  saveTokens,
  clearTokens,
  getStoredToken,
  isE2ETokenAuthEnabled,
} from './keycloak';

export interface User {
  id: string;
  username: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  login: () => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

function decodeJwtPayload(token: string): {
  sub?: string;
  preferred_username?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  realm_access?: { roles?: string[] };
  resource_access?: { [key: string]: { roles?: string[] } };
} {
  const payload = token.split('.')[1] || '';
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  return JSON.parse(atob(padded)) as ReturnType<typeof decodeJwtPayload>;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const initializingRef = useRef(false);

  const updateUserFromParsed = useCallback((parsed: {
    sub?: string;
    preferred_username?: string;
    email?: string;
    name?: string;
    given_name?: string;
    family_name?: string;
    realm_access?: { roles?: string[] };
    resource_access?: { [key: string]: { roles?: string[] } };
  }) => {
    // Get roles from realm_access and resource_access
    const realmRoles = parsed.realm_access?.roles || [];
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'dsc-app';
    const clientRoles = parsed.resource_access?.[clientId]?.roles || [];
    const allRoles = [...new Set([...realmRoles, ...clientRoles])];

    setUser({
      id: parsed.sub || '',
      username: parsed.preferred_username || '',
      email: parsed.email,
      name: parsed.name,
      firstName: parsed.given_name,
      lastName: parsed.family_name,
      roles: allRoles,
    });
  }, []);

  // Extract user info from Keycloak token
  const updateUser = useCallback(() => {
    if (!keycloak?.tokenParsed) {
      setUser(null);
      return;
    }

    const parsed = keycloak.tokenParsed as {
      sub?: string;
      preferred_username?: string;
      email?: string;
      name?: string;
      given_name?: string;
      family_name?: string;
      realm_access?: { roles?: string[] };
      resource_access?: { [key: string]: { roles?: string[] } };
    };

    updateUserFromParsed(parsed);
  }, [updateUserFromParsed]);

  // Refresh token with retry and exponential backoff
  const refreshToken = useCallback(async (retries = 3) => {
    if (isE2ETokenAuthEnabled()) return;
    if (!keycloak) return;

    for (let i = 0; i < retries; i++) {
      try {
        const refreshed = await keycloak.updateToken(MIN_TOKEN_VALIDITY);
        if (refreshed) {
          saveTokens();
          setToken(keycloak.token || null);
        }
        return; // success
      } catch {
        if (i < retries - 1) {
          await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
        }
      }
    }
    // all retries failed
    console.error('Failed to refresh token after retries');
    clearTokens();
    keycloak.login();
  }, []);

  // Initialize Keycloak
  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initializingRef.current) return;
    initializingRef.current = true;

    let refreshInterval: ReturnType<typeof setInterval>;

    const initKeycloak = async () => {
      try {
        if (isE2ETokenAuthEnabled()) {
          const storedToken = getStoredToken();
          if (storedToken) {
            const parsed = decodeJwtPayload(storedToken);
            setIsAuthenticated(true);
            setToken(storedToken);
            updateUserFromParsed(parsed);
            return;
          }
        }
        const authenticated = await keycloak.init(initOptions);
        setIsAuthenticated(authenticated);
        setToken(keycloak.token || null);

        if (authenticated) {
          saveTokens();
          updateUser();
        }

        // Save tokens on every successful refresh
        keycloak.onAuthRefreshSuccess = () => {
          saveTokens();
          setToken(keycloak.token || null);
        };

        // Set up token refresh
        refreshInterval = setInterval(
          refreshToken,
          TOKEN_REFRESH_INTERVAL,
        );

        // Handle token expiry
        keycloak.onTokenExpired = () => {
          refreshToken();
        };
      } catch (error) {
        console.error('Keycloak init failed:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    // Refresh token when tab becomes visible (after sleep/background)
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshToken();
    };
    document.addEventListener('visibilitychange', onVisible);

    initKeycloak();

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshToken, updateUserFromParsed, updateUser]);

  const login = useCallback(() => {
    keycloak.login();
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const hasRole = useCallback(
    (role: string) => {
      return user?.roles.includes(role) || false;
    },
    [user],
  );

  const hasAnyRole = useCallback(
    (roles: string[]) => {
      return roles.some((role) => user?.roles.includes(role)) || false;
    },
    [user],
  );

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    token,
    login,
    logout,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
